<script setup lang="ts">
import { useTheme } from "@/composable/useTheme";
import { trackReportFeature } from "@/analytics/report-analytics";
import { EventType } from "@/types";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import {
  useCreateEventLog,
  useGetReport,
  useGetFieldObservations,
} from "@/stores/queries";
import { useRoute, useRouter } from "vue-router";
import $ls, { LocalStorageKey } from "@/utils/local-storage";
import ReportState, { ReportSection } from "@/stores/report/ReportState";
import ReportMetadataSection from "@/components/report-tool/steps/ReportMetadataStep.vue";
import ReportPhotosStep from "@/components/report-tool/steps/photos/ReportPhotosStep.vue";
import ReportFindingsStep from "@/components/report-tool/steps/findings/ReportFindingsStep.vue";
import ReportOverviewsStep from "@/components/report-tool/steps/overviews/ReportOverviewsStep.vue";
import ReportConclusionsStep from "@/components/report-tool/steps/ReportConclusionsStep.vue";

import {
  HEADER_HEIGHT,
  TABS_HEIGHT,
} from "@/components/report-tool/layout/constants";
import GenerateReportBtn from "@/components/report-tool/layout/GenerateReportBtn.vue";
import ReportTabs from "@/components/report-tool/layout/ReportTabs.vue";
import HeaderTemplateSelector from "@/components/report-tool/layout/HeaderTemplateSelector.vue";
import TemplateSelectModal from "@/components/report-tool/layout/TemplateSelectModal.vue";

import { useLanguage } from "@/composable/useLanguage";
import OnlineOnlyView from "../OnlineOnlyView.vue";
import { useDeviceState } from "@/stores/DeviceState";
import TemplateDrawer from "@/components/report-templates/TemplateDrawer.vue";
import AIAssistantHub from "@/components/report-tool/ai/AIAssistantHub.vue";
import AIFirstTimeBanner from "@/components/report-tool/ai/AIFirstTimeBanner.vue";

useLanguage();

onMounted(() => {
  createEvent(EventType.USE_REPORT_TOOL);
});

const route = useRoute();
const router = useRouter();

const reportId = computed(() => {
  return route.params.reportId ? Number(route.params.reportId) : undefined;
});

const { data: report } = useGetReport(reportId);

// Computed accessors for backward compatibility
const diagnostic = computed(() => report.value?.diagnostic);
const diagnosticId = computed(() => report.value?.diagnosticId);

const showTemplateModal = ref<boolean>(false);

// Valid tab values for URL
const validTabs = [
  ReportSection.GENERAL,
  ReportSection.PHOTOS,
  ReportSection.FINDINGS,
  ReportSection.OVERVIEWS,
  ReportSection.CONCLUSIONS,
];

// Read tab from URL query parameter
const getTabFromRoute = (): ReportSection | null => {
  const tab = route.query.tab as string;
  if (tab && validTabs.includes(tab as ReportSection)) {
    return tab as ReportSection;
  }
  return null;
};

/**
 * Onglet demandé par l'URL, capturé au montage et conservé jusqu'à ce qu'il
 * puisse être appliqué. Un rapport sans template ouvre d'abord la sélection de
 * template : sans cette mémoire, l'intention (« ?tab=findings », venu de la
 * génération IA depuis l'outil dessin) serait perdue au profit de l'onglet
 * général.
 */
const pendingTab = ref<ReportSection | null>(getTabFromRoute());

const applyPendingTab = (fallback: ReportSection) => {
  ReportState.setActiveSection(pendingTab.value ?? fallback);
  pendingTab.value = null;
};

watch(
  report,
  (newReport, oldReport) => {
    // Seulement au premier chargement (pas de oldReport et newReport existe)
    if (!oldReport && newReport) {
      if (newReport.templateId) {
        // Template par défaut existe, l'utiliser automatiquement
        ReportState.setActiveTemplate(newReport.templateId);
        // Onglet demandé dans l'URL, sinon général
        applyPendingTab(ReportSection.GENERAL);
      } else {
        // Pas de template par défaut, ouvrir la modal de sélection
        showTemplateModal.value = true;
        ReportState.setActiveSection(ReportSection.SELECT_TEMPLATE);
      }
    }
  },
  {
    immediate: true,
  },
);

// Sync active section to URL
watch(
  () => ReportState.activeSection,
  (newSection) => {
    trackReportFeature("step_viewed", { step: newSection });
    if (validTabs.includes(newSection)) {
      router.replace({
        query: { ...route.query, tab: newSection },
      });
    }
  },
);

const { createEvent } = useCreateEventLog();

useTheme("light");

async function back() {
  const href = $ls.getItem(LocalStorageKey.BACK_PATH);
  if (href) {
    window.location.href = href;
  } else {
    router.push({ name: "home" });
  }
}
const deviceState = useDeviceState();

onMounted(() => {
  deviceState.startMonitoringOnlineState();
});

onUnmounted(() => {
  deviceState.stopMonitoringOnlineState();
});

const templateId = computed(() => ReportState.activeTemplateId);
const showTemplateDrawer = ref<boolean>(false);
const drawerInitialTab = ref<string | undefined>();

const openTemplateModal = () => {
  showTemplateModal.value = true;
};

const closeTemplateModal = () => {
  showTemplateModal.value = false;
  // Le template vient d'être choisi : on rejoint enfin l'onglet demandé.
  if (pendingTab.value && ReportState.activeSection !== pendingTab.value) {
    applyPendingTab(ReportState.activeSection);
  }
};

const openTemplateSettingsTab = () => {
  drawerInitialTab.value = "settings";
  showTemplateDrawer.value = true;
};

const closeTemplateDrawer = () => {
  showTemplateDrawer.value = false;
};

const projectName = computed(() => diagnostic.value?.project?.name);

// ==========================================
// AI Assistant Hub
// ==========================================

// Convert diagnosticId to Ref<number> for field observations query
const diagnosticIdForQuery = computed(() => diagnosticId.value ?? 0);

// Field observations for AI banner
const { data: fieldObservations } = useGetFieldObservations(
  diagnosticIdForQuery as unknown as import("vue").Ref<number>,
);

const hasFieldObservations = computed(
  () => (fieldObservations.value?.length ?? 0) > 0,
);

/**
 * L'IA n'a pas encore servi sur ce rapport. `isGeneratingFindings` compte aussi :
 * en arrivant de l'outil dessin la génération vient d'être lancée côté serveur,
 * `lastFindingsGeneratedAt` n'est pas encore posé — le bandeau proposerait de
 * générer ce qui est déjà en train de l'être.
 */
const hasNoSections = computed(
  () =>
    !report.value?.lastFindingsGeneratedAt &&
    !report.value?.isGeneratingFindings,
);

// Banner dismissed state (persisted in localStorage per report)
const getBannerDismissedKey = (id: number) =>
  `ai-banner-dismissed-report-${id}`;
const isBannerDismissed = ref(false);

// Load dismissed state on mount
watch(
  reportId,
  (id) => {
    if (id) {
      isBannerDismissed.value =
        localStorage.getItem(getBannerDismissedKey(id)) === "true";
    }
  },
  { immediate: true },
);

// Tabs where the AI banner should be shown
const bannerAllowedTabs = [
  ReportSection.PHOTOS,
  ReportSection.FINDINGS,
  ReportSection.OVERVIEWS,
];

// Show first-time banner
const showFirstTimeBanner = computed(() => {
  return (
    hasFieldObservations.value &&
    hasNoSections.value &&
    !isBannerDismissed.value &&
    !!templateId.value && // Only show when template is selected
    bannerAllowedTabs.includes(ReportState.activeSection) // Only on specific tabs
  );
});

// Dismiss banner
const dismissBanner = () => {
  isBannerDismissed.value = true;
  if (reportId.value) {
    localStorage.setItem(getBannerDismissedKey(reportId.value), "true");
  }
};

// AI Hub ref for triggering drawer from banner
const aiHubRef = ref<InstanceType<typeof AIAssistantHub> | null>(null);

const openAIHub = () => {
  dismissBanner();
  // Trigger findings generation via AIAssistantHub
  aiHubRef.value?.triggerFindingsGeneration();
};
</script>

<template>
  <OnlineOnlyView container-classes="p-0 m-0  ">
    <div class="d-flex flex-column vh-100">
      <header
        class="report-toolbar d-flex align-items-center px-3 fixed-top"
        :style="`height: ${HEADER_HEIGHT}px; z-index: 2`"
      >
        <div class="w-100 d-flex align-items-center gap-4">
          <div class="pe-3">
            <button
              class="btn btn-dark btn-sm rounded d-flex align-items-center gap-2"
              @click="back"
            >
              <el-icon size="24">
                <i class="bi-arrow-left"></i>
              </el-icon>
              <span>{{ $t("back") }}</span>
            </button>
          </div>

          <div class="d-flex flex-column">
            <small v-if="projectName" class="text-white-50">
              {{ projectName }}
            </small>
            <div class="fs-5">{{ diagnostic?.name ?? "Aucun nom" }}</div>
          </div>

          <div class="flex-grow-1"></div>

          <div class="d-flex align-items-center gap-3">
            <HeaderTemplateSelector
              @click="openTemplateModal"
              @settings="openTemplateSettingsTab"
            />
            <GenerateReportBtn
              v-if="reportId"
              :report-id="reportId"
              :report-name="diagnostic?.name"
              :has-template="!!templateId"
            />
          </div>
        </div>
      </header>

      <ReportTabs v-if="templateId" />

      <!-- First-time AI Banner -->
      <AIFirstTimeBanner
        v-if="showFirstTimeBanner"
        :observations-count="fieldObservations?.length ?? 0"
        :style="`margin-top: ${HEADER_HEIGHT + TABS_HEIGHT}px`"
        @generate="openAIHub"
        @dismiss="dismissBanner"
      />

      <div v-if="report && diagnostic" class="d-flex flex-grow-1">
        <ReportMetadataSection
          v-if="ReportState.activeSection === ReportSection.GENERAL"
          :report="report"
          :diagnostic="diagnostic"
        />

        <ReportPhotosStep
          v-if="ReportState.activeSection === ReportSection.PHOTOS"
          :diagnostic="diagnostic"
          :report-id="report.id"
        />

        <ReportFindingsStep
          v-if="ReportState.activeSection === ReportSection.FINDINGS"
          :diagnostic="diagnostic"
          :report-id="report.id"
        />

        <ReportOverviewsStep
          v-if="ReportState.activeSection === ReportSection.OVERVIEWS"
          :diagnostic="diagnostic"
          :report-id="report.id"
        />

        <ReportConclusionsStep
          v-if="ReportState.activeSection === ReportSection.CONCLUSIONS"
          :report="report"
          :diagnostic="diagnostic"
        />

        <!-- Annexes hidden for now, component kept for future use -->
        <!-- <ReportAppendiceStep
            v-if="ReportState.activeSection === ReportSection.APPENDICES"
            :diagnostic="diagnostic"
          /> -->
      </div>
    </div>

    <!-- AI Assistant Hub (floating button + popup) -->
    <AIAssistantHub
      v-if="diagnosticId && templateId && diagnostic && reportId && report"
      ref="aiHubRef"
      :diagnostic-id="diagnosticId"
      :diagnostic="diagnostic"
      :report-id="reportId"
      :report="report"
      :report-template-id="templateId"
      :show-pulse="showFirstTimeBanner"
    />
  </OnlineOnlyView>

  <TemplateSelectModal
    v-model:visible="showTemplateModal"
    :reportId="reportId"
    @close="closeTemplateModal"
  />

  <TemplateDrawer
    :template-id="showTemplateDrawer ? templateId : undefined"
    :initial-tab="drawerInitialTab"
    @close="closeTemplateDrawer"
    hide-actions
  />
</template>

<style lang="scss">
@import "@/style/variables.scss";

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.report-toolbar {
  background-color: $dark !important;
  color: white;
}
</style>
